import { useState } from 'react'
import Box from '@mui/system/Box'
import useMediaQuery from '@mui/material/useMediaQuery'
import Icon from '@mui/material/Icon'
import Typography from '@mui/material/Typography'
import AdxPopper from 'components/reusable/AdxPopper'
import { useLocation, useNavigate } from 'react-router-dom'
import { handleView } from '../../functions'
import WarningDeleteModal from 'module/claim/components/prompt-modal/WarningDeleteModal'
import { deleteExpense } from 'services/api/modules/claim/expense'
import EditExpenseModal from 'module/claim/pages/expense/view/EditExpenseModal'
import { parse } from 'date-fns'
import AdxPdfImageViewer from 'components/reusable/AdxPdfImageViewer'

export const ExpenseList = ({
  id,
  reportData,
  category,
  categoryData,
  date,
  merchant,
  distance,
  description,
  amount,
  proofLink,
  file,
  isDraft,
}) => {
  const { id: reportId } = reportData
  const isExtraSmall = useMediaQuery((theme) => theme.breakpoints.down('lg'))
  const [isEditModalOpen, setEditModalOpen] = useState(false)
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false)

  const navigate = useNavigate()
  const location = useLocation()
  const backgroundLocation = location

  return (
    <Box
      onClick={() =>
        isExtraSmall
          ? navigate(`expense/${id}`, {
              state: {
                backgroundLocation,
              },
            })
          : null
      }
      width={1}
      bgcolor="white.main"
      borderRadius="4.5px"
      padding={{ xs: 2, lg: 3 }}
      sx={{ boxShadow: '0px 0px 10px lightgray' }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box width={{ xs: 1, lg: 4 / 5 }} display="flex" alignItems="center" columnGap={2}>
          <Box
            width={90}
            height={90}
            border="2px dashed"
            borderColor="gray-line.main"
            borderRadius={1}
            onClick={() => handleView(file?.url)}
            sx={{
              cursor: 'pointer',
              objectFit: 'cover',
              objectPosition: '0% 0%',
              overflow: 'hidden',
            }}
          >
            <AdxPdfImageViewer fileUrl={file?.url} />
          </Box>

          <Box
            width={{ xs: 1, lg: 'calc(100% - 100px)' }}
            height={85}
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
          >
            <Box>
              <Typography
                noWrap
                component="span"
                variant="body1"
                color="alternate.main"
                fontWeight="bold"
                textTransform="capitalize"
              >
                {category}
              </Typography>
              <Box
                display={'flex'}
                alignItems={'center'}
                columnGap={1.5}
                mt={0.5}
                sx={{
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: { xs: 'start', md: 'center' },
                  justifyContent: 'start',
                }}
              >
                <Typography
                  noWrap
                  component="span"
                  variant="body2"
                  color="light-gray-text.main"
                  textTransform="capitalize"
                >
                  {date}
                </Typography>
                <Icon
                  sx={{
                    display: { xs: 'none', md: 'block' },
                    fontSize: 7,
                    color: 'light-gray-text.main',
                  }}
                >
                  circle_rounded
                </Icon>
                <Typography
                  noWrap
                  component="span"
                  variant="body2"
                  color="light-gray-text.main"
                  textTransform="capitalize"
                >
                  {categoryData.claim_type === 'Mileage' ? `${distance} km` : merchant}
                </Typography>
              </Box>
            </Box>
            <Typography
              display={{ xs: 'none', lg: 'block' }}
              minWidth={400}
              variant="body2"
              color="light-gray-text.main"
              sx={{
                '&:first-letter': {
                  textTransform: 'uppercase',
                },
              }}
            >
              {description}
            </Typography>
            <Typography
              display={{ xs: 'flex', lg: 'none' }}
              noWrap
              component="span"
              variant="h4"
              fontWeight="bold"
              color="alternate.main"
              sx={{
                textDecoration: 'underline',
              }}
            >
              RM {parseFloat(amount).toFixed(2)}
            </Typography>
          </Box>
        </Box>
        <Box
          width={1 / 5}
          display={{ xs: 'none', lg: 'flex' }}
          justifyContent="end"
          alignItems="center"
          columnGap={1}
        >
          <Typography
            noWrap
            component="span"
            variant="h4"
            fontWeight="bold"
            color="alternate.main"
            sx={{
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            RM {parseFloat(amount).toFixed(2)}
          </Typography>
          <AdxPopper
            disablePortal
            linePlacement={isDraft && [2]}
            placement="bottom-end"
            sx={{ width: 220, boxShadow: '0px 0px 10px lightgray' }}
          >
            {isDraft && (
              <Typography
                variant="body1"
                color="dark-text.main"
                onClick={() => setEditModalOpen(true)}
              >
                Edit expense
              </Typography>
            )}

            {isDraft && (
              <Typography
                variant="body1"
                color="dark-text.main"
                onClick={() => setDeleteModalOpen(true)}
              >
                Delete expense
              </Typography>
            )}
            <Typography
              variant="body1"
              color="dark-text.main"
              onClick={() => handleView(file?.url)}
            >
              View attachment
            </Typography>
          </AdxPopper>
          {/* EDIT EXPENSE MODAL */}
          <EditExpenseModal
            isEditModalOpen={isEditModalOpen}
            setEditModalOpen={setEditModalOpen}
            reportData={reportData}
            expenseData={{
              id: id,
              date: parse(date, 'dd/MM/yyyy', new Date()),
              merchant,
              distance,
              description,
              amount: parseFloat(amount).toFixed(2),
              proof_link: proofLink,
              file,
            }}
            previousSelectedCategory={categoryData}
          />
          {/* DELETE EXPENSE MODAL */}
          <WarningDeleteModal
            id={id}
            deleteAPI={() => deleteExpense(reportId, id)}
            reactQueryKeys={['singleReportData']}
            title={`Delete ${category}?`}
            subtitle="You will lose all data under this expense."
            isOpen={isDeleteModalOpen}
            closeModalFunction={setDeleteModalOpen}
          />
        </Box>
      </Box>
    </Box>
  )
}
